import React from "react";
import { Plus, Minus, ShoppingBag } from "lucide-react";
import { formatMoney, itName, itLineTotal } from "../../../lib/retailFormat";
import { lineSpiceFor as spiceOf } from "../../../lib/retailFormat";
import TakeoutBadge from "../TakeoutBadge";

function Row({ label, value, muted }) {
  return (
    <div className={"flex items-center justify-between " + (muted ? "text-emerald-200/70" : "text-emerald-100")}>
      <span className="text-xs uppercase tracking-wider">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

// Phase 3M — extracted verbatim from RetailCustomer.jsx::OrderPanel.
// Persistent right-side cart on lg+. Shows the server-priced cart tax
// and total; it never computes tax itself.
export default function CartPanel({
  cart, currency, customer, orderType, labelFor, onSetQty, onRemove, onCheckout,
  // Item-level Takeout marker (Sep 2026). Optional: a caller that does
  // not pass `onSetItemTakeout` renders exactly as before, with no
  // control and no badge column.
  onSetItemTakeout,
}) {
  const items = cart?.items || [];
  // Only offered while the OVERALL order is Dine-In / Walk-In — "pack
  // this one to go" is meaningless on an order that is already going
  // out of the door. Marking an item NEVER converts the order: the
  // fulfillment mode, totals, tax and scheduling are untouched.
  const takeoutOfferable = Boolean(onSetItemTakeout)
    && (orderType === "dine_in" || orderType === "walk_in");
  const subtotal = Number(cart?.subtotal ?? 0);
  // Product tax treatment (Oct 2026) — tax and total are the SERVER's:
  // the cart is priced by the same engine as checkout, so tax-exempt and
  // custom-rate products are already reflected. A cart without figures
  // shows "calculated at checkout" — never an invented flat-rate amount.
  const hasServerTotals = cart?.tax != null && cart?.total != null;
  const taxLabel = hasServerTotals ? formatMoney(Number(cart.tax), currency) : "Calculated at checkout";
  const totalLabel = hasServerTotals ? formatMoney(Number(cart.total), currency) : formatMoney(subtotal, currency);
  return (
    <aside
      className="hidden lg:flex flex-col w-[320px] xl:w-[360px] bg-[#0a1a0e] text-emerald-50 border-l-4 border-orange-500 shrink-0 lg:h-screen"
      data-testid="retail-order-panel"
    >
      <div className="px-4 py-3 border-b border-emerald-900/40">
        <div className="flex items-center gap-2 font-serif text-lg">
          <ShoppingBag className="w-4 h-4 text-emerald-200" /> Your Order
        </div>
        <div className="text-xs text-emerald-200/70 mt-0.5">
          {customer?.name || "Guest"} · {(() => {
            const base = labelFor ? labelFor(orderType) : ({ dine_in: "Dine-In", takeout: "Takeout", pickup: "Pickup", delivery: "Local Delivery" }[orderType] || "Order");
            return orderType === "dine_in" && customer?.tableNumber ? `${base} · ${customer.tableNumber}` : base;
          })()}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {items.length === 0 ? (
          <div className="text-center text-sm italic text-emerald-200/60 py-12" data-testid="retail-order-panel-empty">
            Browse the menu and tap ADD<br/>to start your order!
          </div>
        ) : items.map((it) => (
          <div key={it.item_id || it.id} className="bg-[#102718] rounded-md border border-emerald-900/40 px-3 py-2 text-sm" data-testid={`retail-order-item-${it.item_id || it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-emerald-50 truncate">{itName(it)}</div>
                {/* The customer's own spice choice, on the line it
                    belongs to. Absent entirely for a product with no
                    spice level — nothing is invented here. */}
                {spiceOf(it) && (
                  <div className="text-[11px] text-amber-200/90 mt-0.5" data-testid={`retail-order-spice-${it.item_id || it.id}`}>
                    🌶 {spiceOf(it).short} · {spiceOf(it).label}
                  </div>
                )}
                {it.item_takeout && (
                  <div className="mt-1"><TakeoutBadge dark testId={`retail-order-item-takeout-badge-${it.item_id || it.id}`} /></div>
                )}
              </div>
              <div className="text-orange-300 font-semibold">{formatMoney(itLineTotal(it), currency)}</div>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <button onClick={() => {
                const next = (it.quantity || 0) - 1;
                if (next <= 0) onRemove(it.item_id || it.id);
                else onSetQty(it.item_id || it.id, next);
              }} data-testid={`retail-order-dec-${it.item_id || it.id}`} className="w-6 h-6 rounded bg-orange-500 text-white grid place-items-center hover:bg-orange-600">
                <Minus className="w-3 h-3" />
              </button>
              <span className="text-emerald-50 text-sm min-w-[1.5ch] text-center">{it.quantity}</span>
              <button onClick={() => onSetQty(it.item_id || it.id, (it.quantity || 0) + 1)} data-testid={`retail-order-inc-${it.item_id || it.id}`} className="w-6 h-6 rounded bg-orange-500 text-white grid place-items-center hover:bg-orange-600">
                <Plus className="w-3 h-3" />
              </button>
              {takeoutOfferable && (
                <button
                  type="button"
                  onClick={() => onSetItemTakeout(it.item_id || it.id, !it.item_takeout)}
                  aria-pressed={!!it.item_takeout}
                  data-testid={`retail-order-item-takeout-${it.item_id || it.id}`}
                  title="Pack this item to take away. The rest of the order stays Dine-In."
                  className={
                    "ml-auto h-8 px-2 rounded text-[10px] font-bold uppercase tracking-wider border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 "
                    + (it.item_takeout
                      ? "bg-amber-400/20 text-amber-200 border-amber-300/50"
                      : "bg-transparent text-emerald-200/70 border-emerald-200/25 hover:text-emerald-50 hover:border-emerald-200/50")
                  }
                >
                  {it.item_takeout ? "Takeout ✓" : "Takeout"}
                </button>
              )}
              {!takeoutOfferable && (
                <span className="ml-auto text-[10px] text-emerald-200/50">+ Note</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 border-t border-emerald-900/40 text-sm">
        <Row label="Subtotal" value={formatMoney(subtotal, currency)} />
        <Row label="Tax" value={taxLabel} muted />
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-emerald-900/50">
          <span className="font-bold text-emerald-50">TOTAL</span>
          <span className="font-extrabold text-2xl text-orange-300" data-testid="retail-order-panel-total">{totalLabel}</span>
        </div>
        <button
          type="button"
          onClick={onCheckout}
          disabled={items.length === 0}
          data-testid="retail-checkout-btn"
          className={
            "mt-3 w-full h-12 rounded-md font-semibold text-white text-base transition " +
            (items.length === 0
              ? "bg-orange-500/40 cursor-not-allowed"
              : "bg-orange-500 hover:bg-orange-600 active:scale-[0.99]")
          }
        >
          Proceed to Checkout →
        </button>
      </div>
    </aside>
  );
}
